import { useCallback } from 'react';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import { TradeTapeItem } from '#/unified-rfqs/entities/shared';
import { findBaseProductCode } from '#/products/domain';
import { TradeTapeItem as DrfqTradeTapeItem } from '../../entities/drfq/trades';
import { useRfqBuilderModalMethods } from '../rfq-builder/modal-context';
import { useMarketPayloadCreator } from './shared';
import useCreateMarket from './useCreateMarket';
import useCreateMarketFromObTrade from './useCreateMarketFromObTrade';
import useCreateMarketFromRfqTrade, {
  transformRfqTradeToPayload,
} from './useCreateMarketFromRfqTrade';

export default function useCreateMarketTradeTape() {
  const marketBuilderModal = useRfqBuilderModalMethods();
  const { createMarket: createMarketFromRfq } = useCreateMarket();
  const makeCreateRFQPayload = useMarketPayloadCreator();
  const grfqService = useGrfqService();
  const createPayloadFromObTrade = useCreateMarketFromObTrade();
  const createPayloadFromRfqTrade = useCreateMarketFromRfqTrade();

  const createMarketFromTrade = useCallback(
    (
      type: 'OB' | 'RFQ',
      trade: DrfqTradeTapeItem,
      onError?: (message: string) => void,
    ) => {
      const payload = makeCreateRFQPayload(
        transformRfqTradeToPayload(trade),
        type,
      );
      marketBuilderModal.open(payload, onError);
    },
    [makeCreateRFQPayload, marketBuilderModal],
  );

  const createMarket = useCallback(
    (
      type: 'OB' | 'RFQ',
      trade: DrfqTradeTapeItem,
      onError?: (message: string) => void,
    ) => {
      if (type === 'OB') {
        createMarketFromTrade('OB', trade, onError);
        return;
      }

      /**
       * Try to create RFQ from original RFQ
       * - if it exists: use RFQ data (to use same account if user is the TAKER)
       * - else: create RFQ from trade as fallback
       */
      createMarketFromRfq({
        rfqId: trade.rfq_id,
        type: 'RFQ',
        onError: () => {
          createMarketFromTrade('RFQ', trade, onError);
        },
      });
    },
    [createMarketFromRfq, createMarketFromTrade],
  );

  const refreshOb = useCallback(
    async (trade: TradeTapeItem, onError: (msg: string) => void) => {
      const payload =
        trade.rfqType === 'drfq'
          ? createPayloadFromRfqTrade(trade)
          : await createPayloadFromObTrade(trade, onError);

      if (payload?.legs == null) return;

      const baseProductCode = findBaseProductCode(
        payload.legs.map((leg) => leg.productCode!),
      );

      const resp = await grfqService.createRfq({
        legs: payload.legs.map((leg) => ({
          instrument: leg.instrumentName!,
          ratio: leg.ratio,
          side: leg.action,
          price: leg.productCode !== baseProductCode ? leg.price : null,
        })),
        venue: trade.venue,
      });
      if (!resp.ok) {
        onError(resp.message);
        return;
      }
      return resp.data;
    },
    [createPayloadFromObTrade, createPayloadFromRfqTrade, grfqService],
  );

  return { createMarket, refreshOb };
}
